import API from "@/API";
import management from "./professionals/management";

const SET_LIST = "SET_LIST";
const SET_PRELOADER = "SET_PRELOADER";
const SET_TOTALS_PAGINATION = "SET_TOTALS_PAGINATION";
const SET_CURRENT_PAGE = "SET_CURRENT_PAGE";
const SET_SORT = "SET_SORT";
const SET_TEXT_SEARCH = "SET_TEXT_SEARCH";
const SET_DESCRIPTION = "SET_DESCRIPTION";
const SET_OPERATION = "SET_OPERATION";
const SET_EVENT = "SET_EVENT";
const SET_DEFAULT_STATE = "SET_DEFAULT_STATE";

const defaultState = () => {
  return {
    list: "",
    pagination: {
      current: 1,
      totalPages: 1,
      totalResults: 1
    },
    sort: "",
    text_search: "",
    preloader: false,
    description: "",
    event: "",
    operation: ""
  };
};

export default {
  namespaced: true,

  state: { ...defaultState() },

  actions: {
    ...management.actions,

    async listAll({ state, commit }) {
      commit("SET_PRELOADER", true);

      try {
        let results = await API.Professionals.index(
          state.pagination.current,
          state.sort,
          state.text_search
        );
        commit("SET_TOTALS_PAGINATION", results.meta);
        commit("SET_LIST", results.data);
      } catch (e) {
        throw e;
      }

      commit("SET_PRELOADER", false);
    },

    async create({}, data) {
      try {
        await API.Professionals.create(data);
      } catch (error) {
        throw error;
      }
    },

    setCurrentPage({ commit, dispatch }, page) {
      commit("SET_CURRENT_PAGE", page);
      dispatch("listAll");
    },

    setSort({ commit, dispatch }, payload) {
      commit("SET_SORT", payload);
      dispatch("listAll");
    },

    setTextSearch({ commit, dispatch }, payload) {
      commit("SET_TEXT_SEARCH", payload);
      commit("SET_CURRENT_PAGE", 1);
      dispatch("listAll");
    },

    setDescription({ commit }, payload) {
      commit("SET_DESCRIPTION", payload);
    },

    setEvent({ commit }, event) {
      commit("SET_EVENT", event);
    },

    setOperation({ commit }, operation) {
      commit("SET_OPERATION", operation);
    },

    resetToDefaultState({ commit }) {
      commit("SET_DEFAULT_STATE");
    },

    async getById({}, id) {
      try {
        return await API.Professionals.getById(id);
      } catch (error) {
        throw error;
      }
    },

    async getOrders({}, id) {
      try {
        return await API.Professionals.getOrders(id);
      } catch (error) {
        throw error;
      }
    },

    async getDistricts({}, id) {
      try {
        return await API.Professionals.getDistricts(id);
      } catch (error) {
        throw error;
      }
    },

    async searchByName({}, { filters, includes }) {
      try {
        return await API.Professionals.searchProfessional(includes, filters);
      } catch (e) {
        throw e;
      }
    },

    async searchByDistrictAndName({}, payload) {
      try {
        return API.Professionals.searchByDistrictAndName(payload);
      } catch (e) {
        throw e;
      }
    },

    async createAvailableEvent({ commit }, data) {
      try {
        return await API.ScheduleEvents.create(data);
      } catch (e) {
        throw e;
      }
    },

    async removeAvailableEvent({}, id) {
      try {
        return await API.ScheduleEvents.remove(id);
      } catch (e) {
        throw e;
      }
    },

    async updateAvailableEvent({}, data) {
      try {
        return await API.ScheduleEvents.update(data);
      } catch (e) {
        throw e;
      }
    },

    async updateProfessionalAccountable({}, professional) {
      try {
        return await API.Professionals.updateProfessional(professional);
      } catch (e) {
        throw e;
      }
    },
    async updateProfessionalAccount({}, account) {
      try {
        return await API.Professionals.updateAccount(account);
      } catch (e) {
        throw e;
      }
    },
    async updateProfessionalProfile({}, profile) {
      try {
        return await API.Professionals.updateProfile(profile);
      } catch (e) {
        throw e;
      }
    },
    async updateProfessionalphones({}, phones) {
      try {
        for await (let phone of phones) {
          return phone.id
            ? API.Professionals.updatePhone(phone)
            : API.Professionals.createPhone(phone, data.account.id);
        }
      } catch (e) {
        throw e;
      }
    },

    async updateProfessional({ dispatch }, professional) {
      try {
        const professionalUpdatableRequest = [
          dispatch("updateProfessionalAccountable", professional),
          dispatch("updateProfessionalAccount", professional.account),
          dispatch("updateProfessionalProfile", professional.account.profile),
          dispatch("updateProfessionalphones", professional.account.phones)
        ];

        return Promise.all(professionalUpdatableRequest);
      } catch (e) {
        throw e;
      }
    },

    async updateRating({}, professional) {
      try {
        await API.Professionals.updateRating(professional);
      } catch (e) {
        throw e;
      }
    },

    async addDistricts({}, payload) {
      try {
        return await API.Professionals.addDistricts(payload);
      } catch (e) {
        throw e;
      }
    },

    async updateProfilePicture({}, payload) {
      try {
        let res = await API.Profile.updateProfilePicture(payload);
        return res;
      } catch (e) {
        throw e;
      }
    },

    async getFeedbacks({}, professional_id) {
      try {
        return await API.Professionals.getFeedbacks(professional_id);
      } catch (e) {
        throw e;
      }
    },

    async addFeedback({}, payload) {
      try {
        return await API.Professionals.addFeedback(payload);
      } catch (e) {
        throw e;
      }
    },

    async removeFeedback({}, feedback_id) {
      try {
        return await API.Professionals.removeFeedback(feedback_id);
      } catch (e) {
        throw e;
      }
    },

    async updateBio({}, payload) {
      try {
        await API.Profile.updateBio(payload);
      } catch (e) {
        throw e;
      }
    }
  },

  mutations: {
    [SET_LIST](state, payload) {
      state.list = payload;
    },
    [SET_PRELOADER](state, payload) {
      state.preloader = payload;
    },
    [SET_TOTALS_PAGINATION](state, payload) {
      state.pagination.totalPages = payload.page_count;
      state.pagination.totalResults = payload.record_count;
    },
    [SET_CURRENT_PAGE](state, payload) {
      state.pagination.current = payload;
    },
    [SET_SORT](state, payload) {
      state.sort = payload;
    },
    [SET_TEXT_SEARCH](state, payload) {
      state.text_search = payload;
    },

    [SET_DESCRIPTION](state, payload) {
      state.description = payload;
    },

    [SET_EVENT](state, event) {
      state.event = event;
    },

    [SET_OPERATION](state, operation) {
      state.operation = operation;
    },

    [SET_DEFAULT_STATE](state) {
      Object.assign(state, defaultState());
    }
  },

  getters: {}
};



// WEBPACK FOOTER //
// ./src/store/modules/professionals.js